import chalk from 'chalk';

export function supportsColor(): boolean {
  if (process.env.NO_COLOR) return false;
  if (process.env.FORCE_COLOR) return true;
  return Boolean(process.stdout.isTTY);
}

export const colors = {
  success: (text: string) => (supportsColor() ? chalk.green(text) : text),
  error: (text: string) => (supportsColor() ? chalk.red(text) : text),
  warning: (text: string) => (supportsColor() ? chalk.yellow(text) : text),
  info: (text: string) => (supportsColor() ? chalk.cyan(text) : text),
  bold: (text: string) => (supportsColor() ? chalk.bold(text) : text),
  dim: (text: string) => (supportsColor() ? chalk.dim(text) : text),
  white: (text: string) => (supportsColor() ? chalk.white(text) : text),
  gray: (text: string) => (supportsColor() ? chalk.gray(text) : text),
  blue: (text: string) => (supportsColor() ? chalk.blue(text) : text),
  url: (text: string) => (supportsColor() ? chalk.blue.underline(text) : text),
};

export const symbols = {
  success: '\u2713', // ✓
  error: '\u2716', // ✖
  warning: '!',
  info: 'i',
  debug: '[debug]',
  bullet: '\u2022', // •
  pointer: '\u276F', // ❯
  line: '\u2500', // ─
};

// Chalk emits SGR sequences only; this is not a full ECMA-48 parser.
const ESC = String.fromCharCode(27);
const ANSI_PATTERN = `${ESC}\\[[0-9;]*[a-zA-Z]`;
const ANSI_GLOBAL = new RegExp(ANSI_PATTERN, 'g');
const ANSI_SEQUENCE = new RegExp(`^${ANSI_PATTERN}`);

export function stripAnsi(str: string): string {
  return str.replace(ANSI_GLOBAL, '');
}

export function pad(str: string, len: number): string {
  const vLen = stripAnsi(str).length;
  return vLen >= len ? str : str + ' '.repeat(len - vLen);
}

// Terminal display columns, not UTF-16 code units. A CJK commit subject measured
// by .length renders at twice its measured width and pushes the box border out.
export function visibleWidth(str: string): number {
  let width = 0;
  for (const ch of stripAnsi(str)) {
    const cp = ch.codePointAt(0)!;
    if (isZeroWidth(cp)) continue;
    width += isWide(cp) ? 2 : 1;
  }
  return width;
}

function isZeroWidth(cp: number): boolean {
  // Combining marks and zero-width joiners/selectors occupy no column of their own.
  return (
    (cp >= 0x0300 && cp <= 0x036f) || (cp >= 0x200b && cp <= 0x200f) || cp === 0xfeff || (cp >= 0xfe00 && cp <= 0xfe0f)
  );
}

function isWide(cp: number): boolean {
  return (
    (cp >= 0x1100 && cp <= 0x115f) ||
    (cp >= 0x2e80 && cp <= 0xa4cf) ||
    (cp >= 0xac00 && cp <= 0xd7a3) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xfe30 && cp <= 0xfe6f) ||
    (cp >= 0xff00 && cp <= 0xff60) ||
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    (cp >= 0x1f300 && cp <= 0x1faff) ||
    (cp >= 0x20000 && cp <= 0x3fffd)
  );
}

// Pads like pad(), but also truncates when the visible content overruns `len`.
// A fixed-width box row must never exceed its budget or the border is pushed out.
export function fit(str: string, len: number): string {
  if (len <= 0) return '';
  const vLen = visibleWidth(str);
  if (vLen === len) return str;
  if (vLen < len) return str + ' '.repeat(len - vLen);

  // Iterate by code point so a surrogate pair is copied or dropped whole; a split
  // pair would emit a lone surrogate and still miscount the column.
  let out = '';
  let width = 0;
  let hasStyle = false;
  let i = 0;
  while (i < str.length) {
    if (str[i] === ESC) {
      const match = ANSI_SEQUENCE.exec(str.slice(i));
      if (match) {
        out += match[0];
        hasStyle = true;
        i += match[0].length;
        continue;
      }
    }
    const ch = String.fromCodePoint(str.codePointAt(i)!);
    const cw = visibleWidth(ch);
    if (width + cw > len - 1) {
      out += '\u2026';
      break;
    }
    out += ch;
    width += cw;
    i += ch.length;
  }
  // Reset styling so a truncated color cannot leak into the border.
  return hasStyle ? `${out}${ESC}[0m` : out;
}

interface BoxOptions {
  borderColor?: 'green' | 'red' | 'yellow' | 'cyan' | 'white';
  title?: string;
  padding?: number;
}

export function box(content: string[], options: BoxOptions = {}): string {
  if (content.length === 0 && !options.title) return '';

  const { padding = 1, borderColor = 'white', title } = options;

  const maxContentWidth = Math.max(
    0,
    ...content.map((line) => stripAnsi(line).length),
    title ? stripAnsi(title).length : 0
  );
  const boxWidth = maxContentWidth + padding * 2 + 2;

  const colorFn =
    borderColor === 'green'
      ? colors.success
      : borderColor === 'red'
        ? colors.error
        : borderColor === 'yellow'
          ? colors.warning
          : borderColor === 'cyan'
            ? colors.info
            : colors.white;

  const lines: string[] = [];

  // Top border with optional title
  if (title) {
    const titleText = ` ${title} `;
    const remainingWidth = boxWidth - 2 - titleText.length;
    const leftPad = Math.floor(remainingWidth / 2);
    const rightPad = remainingWidth - leftPad;
    lines.push(
      colorFn('\u250C') +
        colorFn('\u2500'.repeat(leftPad)) +
        colors.bold(titleText) +
        colorFn('\u2500'.repeat(rightPad)) +
        colorFn('\u2510')
    );
  } else {
    lines.push(colorFn('\u250C') + colorFn('\u2500'.repeat(boxWidth - 2)) + colorFn('\u2510'));
  }

  // Top padding
  if (padding > 0) {
    lines.push(colorFn('\u2502') + ' '.repeat(boxWidth - 2) + colorFn('\u2502'));
  }

  // Content lines
  for (const line of content) {
    const lineLength = stripAnsi(line).length;
    const rightSpace = boxWidth - 2 - padding - lineLength - padding;
    lines.push(
      colorFn('\u2502') + ' '.repeat(padding) + line + ' '.repeat(Math.max(0, rightSpace + padding)) + colorFn('\u2502')
    );
  }

  // Bottom padding
  if (padding > 0) {
    lines.push(colorFn('\u2502') + ' '.repeat(boxWidth - 2) + colorFn('\u2502'));
  }

  // Bottom border
  lines.push(colorFn('\u2514') + colorFn('\u2500'.repeat(boxWidth - 2)) + colorFn('\u2518'));

  return lines.join('\n');
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes}m ${remainingSeconds}s`;
}

// OSC 8 makes the text itself clickable in a terminal; CI log viewers do not
// render it, so there the URL is printed in full, where they link it.
export function hyperlink(text: string, url: string): string {
  if (process.env.CI || process.env.NO_COLOR || !process.stderr.isTTY) return `${text}: ${url}`;
  return `\u001b]8;;${url}\u0007${colors.url(text)}\u001b]8;;\u0007`;
}
